import React, { useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Button, Modal } from 'grab-ui';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import { groupAndSumFailedPayments } from 'helper/failed-payment-helper';
import _flatten from 'lodash/flatten';
import _uniqueID from 'lodash/uniqueId';
import { formatMajorUnitsForDisplay } from 'helper/order';
import paymentIcon from 'components/booking/payment-field/payment-icon';
import { isAdminSelector } from 'selectors/login-selector';
import { isPaxSelector } from 'selectors/app-selector';
import { pushErrorModalAction } from 'actions/app-actions';
import { ERROR_MODAL_TYPE } from 'components/v2/error-modal/error-modal';
import { APP_BASE_URL_NAME } from 'constants/app-constants';
import RoutesV2 from 'RoutesV2';

import css from './failed-payments-modal.less';

const FailedPaymentsListModal = ({
  failedPayments,
  failedPaymentsRetryStatus,
  setFailedPaymentsRetryStatus,
  paymentMethods,
  isUserAdmin,
  isPax,
}) => {
  const dispatch = useDispatch();
  const { listFailedPaymentsModalVisible } = failedPaymentsRetryStatus;

  const handleCancel = () => {
    setFailedPaymentsRetryStatus({
      listFailedPaymentsModalVisible: false,
    });
  };

  const groupedPayments = groupAndSumFailedPayments(failedPayments);
  const flattenedPayments = _flatten(
    Object.keys(groupedPayments).map(currency => groupedPayments[currency]),
  );

  const errorPaymentActionModal = useCallback(
    ({ description, okButtonText, extraButtonText, onExtraButtonHandler }) => {
      dispatch(
        pushErrorModalAction({
          message: I18n.t('v2.failedPayments.noPaymentAvailableTitle'),
          description,
          okButtonText,
          extraButtonText,
          type: ERROR_MODAL_TYPE.WARNING,
          onExtraButton: () => {
            onExtraButtonHandler();
          },
        }),
      );
    },
    [],
  );

  const addNewPaymentMethod = () => {
    window.open(
      `${window.location.origin}/${APP_BASE_URL_NAME}/${
        RoutesV2.redirectGfb.key
      }`,
    );
  };

  const handleOk = () => {
    setFailedPaymentsRetryStatus({
      listFailedPaymentsModalVisible: false,
    });
    // if there is a cashless payment method, show retry if not show error modal
    if (paymentMethods.length !== 0) {
      setFailedPaymentsRetryStatus({
        retryFailedPaymentsModalVisible: true,
      });
    } else if (isPax) {
      errorPaymentActionModal({
        description: I18n.t('v2.failedPayments.noPaymentAvailableBodyPax'),
        okButtonText: I18n.t(
          'v2.failedPayments.noPaymentAvailablePaxCloseButton',
        ),
      });
    } else if (isUserAdmin) {
      errorPaymentActionModal({
        description: I18n.t('v2.failedPayments.noPaymentAvailableBodyAdmin'),
        okButtonText: I18n.t(
          'v2.failedPayments.noPaymentAvailableAdminCancelButton',
        ),
        extraButtonText: I18n.t(
          'v2.failedPayments.noPaymentAvailableAdminAddButton',
        ),
        onExtraButtonHandler: addNewPaymentMethod,
      });
    } else {
      errorPaymentActionModal({
        description: I18n.t('v2.failedPayments.noPaymentAvailableBodyNonAdmin'),
        okButtonText: I18n.t(
          'v2.failedPayments.noPaymentAvailableNonAdminOkButton',
        ),
      });
    }
  };

  return (
    <Modal
      visible={listFailedPaymentsModalVisible}
      footer={[
        <Button key="back" onClick={handleCancel} className={css.cancelButton}>
          {I18n.t('v2.failedPayments.paymentListCancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleOk}
          className={css.mainButton}
        >
          {I18n.t('v2.failedPayments.settlePaymentButton')}
        </Button>,
      ]}
      width={608}
      closable={false}
      className={css.modal}
    >
      <div>
        <p className={css.title}>
          {I18n.t('v2.failedPayments.settlePaymentTitle')}
        </p>
        <p className={css.subtitle}>
          {I18n.t('v2.failedPayments.settlePaymentBody')}
        </p>

        {flattenedPayments.map(p => (
          <div key={_uniqueID()} className={css.failedPaymentRow}>
            <div className={css.descriptionContainer}>
              {Boolean(paymentIcon[p.paymentType]) && (
                <img
                  className={css.paymentIcon}
                  src={paymentIcon[p.paymentType]}
                  alt=""
                />
              )}
              {p.refNumber ? (
                <div className={css.twoRowDescription}>
                  <span>{p.refNumber}</span>
                  <span className={css.paymentType}>{p.paymentType}</span>
                </div>
              ) : (
                <div className={css.oneRowDescription}>{p.description}</div>
              )}
            </div>
            <div className={css.total}>
              {formatMajorUnitsForDisplay(p.total, { code: p.currency })}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};

FailedPaymentsListModal.propTypes = {
  failedPayments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  failedPaymentsRetryStatus: PropTypes.shape({}).isRequired,
  setFailedPaymentsRetryStatus: PropTypes.func.isRequired,
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isUserAdmin: PropTypes.bool.isRequired,
  isPax: PropTypes.bool.isRequired,
};

export const mapStateToProps = state => ({
  isUserAdmin: isAdminSelector(state),
  isPax: isPaxSelector(state),
});

export default connect(mapStateToProps)(FailedPaymentsListModal);
